import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CountryPicker } from "@/components/CountryPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { toastApiError, toastApiSuccess } from "@/lib/api-toast";
import type { components } from "@dolfin/api-admin";

type CompanyResponse = components["schemas"]["CompanyResponse"];

interface EditCompanyDialogProps {
  company: CompanyResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (company: CompanyResponse) => void;
}

/** Sends only changed fields: PATCH treats omitted as "leave alone" and "" as "clear". No routing overrides
 * here: the API can't clear them back to the client default, so a control could never switch off. */
export function EditCompanyDialog({ company, open, onOpenChange, onSaved }: EditCompanyDialogProps) {
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Refill on open, so a cancelled edit doesn't linger.
  useEffect(() => {
    if (!open || !company) return;
    setName(company.name ?? "");
    setContactEmail(company.contactEmail ?? "");
    setAddressLine1(company.addressLine1 ?? "");
    setAddressLine2(company.addressLine2 ?? "");
    setCity(company.city ?? "");
    setPostalCode(company.postalCode ?? "");
    setCountry(company.country ?? "");
  }, [open, company]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!company?.id) return;

    const body: Record<string, string> = {};
    const changed = (next: string, before: string | null | undefined) => next !== (before ?? "");

    if (changed(name.trim(), company.name)) body.name = name.trim();
    if (changed(contactEmail.trim(), company.contactEmail)) body.contactEmail = contactEmail.trim();
    if (changed(addressLine1.trim(), company.addressLine1)) body.addressLine1 = addressLine1.trim();
    if (changed(addressLine2.trim(), company.addressLine2)) body.addressLine2 = addressLine2.trim();
    if (changed(city.trim(), company.city)) body.city = city.trim();
    if (changed(postalCode.trim(), company.postalCode)) body.postalCode = postalCode.trim();
    if (changed(country, company.country)) body.country = country;

    if (Object.keys(body).length === 0) {
      onOpenChange(false);
      return;
    }

    setSubmitting(true);
    try {
      const { data, error, response } = await api.PATCH("/v1/companies/{companyId}", {
        params: { path: { companyId: company.id } },
        body,
      });
      if (error || !data) {
        await toastApiError({ error, response }, "Couldn't save the company.");
        return;
      }
      toastApiSuccess("Company updated");
      onSaved(data);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit company</DialogTitle>
            <DialogDescription>
              Changing the head office address changes routing for future bills — it is the address a
              group-level bill is recognised by.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-company-name">Name</Label>
              <Input
                id="edit-company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ozone Coffee"
                required
                autoFocus
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-company-contact-email">Contact email</Label>
              <Input
                id="edit-company-contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="ap@ozonecoffee.com"
              />
              <p className="text-xs text-muted-foreground">
                Changing this does not change who may forward bills — the allowed senders list is
                managed separately, and an address allowed when the company was created stays allowed.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-company-address">Head office address</Label>
              <Input
                id="edit-company-address"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="11 Leonard Street"
              />
              <Input
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder="Address line 2 (optional)"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="London" />
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="EC2A 4AQ"
                />
              </div>
              <CountryPicker value={country} onChange={setCountry} />
              <p className="text-xs text-muted-foreground">
                Bills delivered here are treated as group costs and always wait for a person, rather
                than being assigned to whichever site happens to share the postcode.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting && <Spinner className="size-3.5" />} Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
